import { useCallback, useEffect } from 'react'

import { Box, Button, ButtonGroup, Group } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { IconCalendar } from '@tabler/icons-react'
import dayjs from 'dayjs'

import classes from './TowerDateRangeSelector.module.css'

export type TimeRangePreset = '1W' | '1M' | '3M' | '6M' | '1Y' | 'YTD' | 'ALL'

interface TowerDateRangeSelectorProps {
  startDate: string
  endDate: string
  onRangeChange: (startDate: string, endDate: string) => void
  defaultTimePreset?: TimeRangePreset
  // selectedDate?: string
  // onSelectDate?: (date: string) => void
  showPresets?: boolean
  size?: 'xs' | 'sm' | 'md'
}

const calculateDateFromPreset = (preset: TimeRangePreset): string => {
  const today = dayjs()
  let start: dayjs.Dayjs

  switch (preset) {
    case '1W':
      start = today.subtract(1, 'week')
      break
    case '1M':
      start = today.subtract(1, 'month')
      break
    case '3M':
      start = today.subtract(3, 'months')
      break
    case '6M':
      start = today.subtract(6, 'months')
      break
    case '1Y':
      start = today.subtract(1, 'year')
      break
    case 'YTD':
      start = today.startOf('year')
      break
    case 'ALL':
    default:
      start = today.subtract(10, 'years')
      break
  }

  return start.format('YYYY-MM-DD')
}

const isPresetActive = (
  preset: TimeRangePreset,
  startDate: string,
  endDate: string,
): boolean => {
  const today = dayjs().format('YYYY-MM-DD')
  // Only consider preset active if end date is today
  if (endDate !== today) return false

  const presetStartDate = calculateDateFromPreset(preset)
  return presetStartDate === startDate
}

export function TowerDateRangeSelector({
  startDate,
  endDate,
  onRangeChange,
  defaultTimePreset = '1M',
  showPresets = true,
  size = 'xs',
}: TowerDateRangeSelectorProps) {
  const applyPreset = useCallback(
    (preset: TimeRangePreset) => {
      const newStart = calculateDateFromPreset(preset)
      const today = dayjs().format('YYYY-MM-DD')
      onRangeChange(newStart, today)
    },
    [onRangeChange],
  )

  useEffect(() => {
    if (!startDate && !endDate) {
      applyPreset(defaultTimePreset)
    }
  }, [applyPreset, defaultTimePreset, endDate, startDate])

  return (
    <Box className={classes.range_controls}>
      {showPresets && (
        <ButtonGroup className={classes.preset_buttons}>
          {(
            [
              '1W',
              '1M',
              '3M',
              'YTD',
              'ALL' /*'6M', '1Y', 'YTD', 'ALL'*/,
            ] as TimeRangePreset[]
          ).map((preset) => (
            <Button
              key={preset}
              size={size}
              variant={
                isPresetActive(preset, startDate, endDate) ? 'filled' : 'light'
              }
              onClick={() => {
                applyPreset(preset)
              }}
            >
              {preset}
            </Button>
          ))}
        </ButtonGroup>
      )}

      <Group className={classes.date_pickers}>
        <DateInput
          size={size}
          valueFormat="YYYY-MM-DD"
          label="From"
          placeholder="Start date"
          value={startDate ? dayjs(startDate).toDate() : null}
          onChange={(date) => {
            const dateStr = date ? dayjs(date).format('YYYY-MM-DD') : ''
            if (dateStr) {
              onRangeChange(dateStr, endDate)
            }
          }}
          leftSection={<IconCalendar size={16} />}
          maxDate={endDate ? dayjs(endDate).toDate() : undefined}
        />

        <DateInput
          size={size}
          valueFormat="YYYY-MM-DD"
          label="To"
          placeholder="End date"
          value={endDate ? dayjs(endDate).toDate() : null}
          onChange={(date) => {
            const dateStr = date ? dayjs(date).format('YYYY-MM-DD') : ''
            if (dateStr) {
              onRangeChange(startDate, dateStr)
            }
          }}
          leftSection={<IconCalendar size={16} />}
          minDate={startDate ? dayjs(startDate).toDate() : undefined}
          maxDate={dayjs().toDate()} // Don't allow future dates
        />
      </Group>
    </Box>
  )
}
